import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useRequireAdmin } from "@/hooks/useRequireAdmin";
import {
  listMembers,
  createInvite,
  removeMember,
  resendConfirmation,
  resetMemberPassword,
  type CompanyMember,
} from "@/lib/api-client";
import { parseApiError } from "@/lib/api-error";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Users,
  Loader2,
  Link2,
  Trash2,
  CheckCircle2,
  Copy,
  MoreHorizontal,
  Mail,
  KeyRound,
} from "lucide-react";

export default function MembersPage() {
  const { t } = useTranslation("members");
  const { isAdmin, isLoading: adminLoading } = useRequireAdmin();
  const { profile } = useAuth();

  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite state
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Remove confirmation
  const [removingMember, setRemovingMember] = useState<CompanyMember | null>(
    null,
  );
  const [removeLoading, setRemoveLoading] = useState(false);

  // Per-member action loading state (keyed by user_id)
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>(
    {},
  );

  const loadMembers = useCallback(async () => {
    if (!profile?.company_id) return;
    try {
      const data = await listMembers(profile.company_id);
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [profile?.company_id]);

  useEffect(() => {
    if (isAdmin) loadMembers();
  }, [isAdmin, loadMembers]);

  async function handleCreateInvite() {
    if (!profile?.company_id) return;
    setInviteLoading(true);
    setError(null);

    try {
      const invite = await createInvite(profile.company_id);
      setInviteUrl(invite.invite_url);
      setCopied(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("errors.inviteCreationFailed"),
      );
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleCopyInvite() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback: select text in a temporary input (non-HTTPS contexts)
      const input = document.createElement("input");
      input.value = inviteUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  }

  async function handleRemoveMember() {
    if (!profile?.company_id || !removingMember) return;
    setRemoveLoading(true);
    setError(null);

    try {
      await removeMember(profile.company_id, removingMember.user_id);
      setRemovingMember(null);
      await loadMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.removeFailed"));
      setRemovingMember(null);
    } finally {
      setRemoveLoading(false);
    }
  }

  async function handleResendConfirmation(member: CompanyMember) {
    if (!profile?.company_id) return;
    setActionLoading((prev) => ({ ...prev, [member.user_id]: true }));
    try {
      await resendConfirmation(profile.company_id, member.user_id);
      toast.success(t("success.resendConfirmation"));
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message);
    } finally {
      setActionLoading((prev) => ({ ...prev, [member.user_id]: false }));
    }
  }

  async function handleResetPassword(member: CompanyMember) {
    if (!profile?.company_id) return;
    setActionLoading((prev) => ({ ...prev, [member.user_id]: true }));
    try {
      await resetMemberPassword(profile.company_id, member.user_id);
      toast.success(t("success.resetPassword"));
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message);
    } finally {
      setActionLoading((prev) => ({ ...prev, [member.user_id]: false }));
    }
  }

  if (adminLoading || loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container max-w-2xl py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("subtitle")}</p>
        </div>
        <Button onClick={handleCreateInvite} disabled={inviteLoading}>
          {inviteLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Link2 className="mr-2 h-4 w-4" />
          )}
          {t("createInviteButton")}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {inviteUrl && (
        <Alert className="mb-6 border-primary/20 bg-primary/5">
          <Link2 className="h-4 w-4 text-primary" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <code className="text-xs break-all flex-1">{inviteUrl}</code>
            <Button variant="outline" size="sm" onClick={handleCopyInvite}>
              {copied ? (
                <>
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5 text-green-600" />
                  {t("invite.copied")}
                </>
              ) : (
                <>
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  {t("invite.copyButton")}
                </>
              )}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4 text-primary" />
            {t("teamSection.title", { count: members.length })}
          </CardTitle>
          <CardDescription>{t("teamSection.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {t("emptyState")}
            </p>
          ) : (
            <div className="divide-y">
              {members.map((member) => {
                const isSelf = member.user_id === profile?.id;
                const displayName =
                  [member.first_name, member.last_name]
                    .filter(Boolean)
                    .join(" ") || t("unknownName");

                return (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">
                          {displayName}
                          {isSelf && (
                            <span className="text-muted-foreground font-normal ml-1">
                              {t("selfIndicator")}
                            </span>
                          )}
                        </p>
                        <Badge
                          variant={
                            member.role === "admin" ? "default" : "secondary"
                          }
                          className="text-[10px] px-1.5 py-0"
                        >
                          {member.role === "admin"
                            ? t("roles.admin")
                            : t("roles.member")}
                        </Badge>
                      </div>
                      {member.email && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {member.email}
                        </p>
                      )}
                    </div>
                    {!isSelf && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-muted-foreground"
                            disabled={!!actionLoading[member.user_id]}
                          >
                            {actionLoading[member.user_id] ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MoreHorizontal className="h-4 w-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleResendConfirmation(member)}
                          >
                            <Mail className="mr-2 h-4 w-4" />
                            {t("actions.resendConfirmation")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleResetPassword(member)}
                          >
                            <KeyRound className="mr-2 h-4 w-4" />
                            {t("actions.resetPassword")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setRemovingMember(member)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t("actions.removeMember")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Remove confirmation dialog */}
      <AlertDialog
        open={!!removingMember}
        onOpenChange={(open) => !open && setRemovingMember(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("removeDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("removeDialog.description", {
                name: [removingMember?.first_name, removingMember?.last_name]
                  .filter(Boolean)
                  .join(" "),
                email: removingMember?.email,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeLoading}>
              {t("removeDialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={removeLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeLoading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("removeDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
